import { useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Order } from "models/Order";
import { useAppSelector } from "app/hooks";
import { selectUserIsAdminOrUserAdmin } from "features/auth/authSlice";
import SelectCompanyDialog from "../dialogs/SelectCompanyDialog";
import {
  useAssignCompanyMutation,
  useEditSubcontractorMutation,
  useUnassignCompanyMutation,
} from "app/api/orders";
import EmptyMessage from "components/EmptyMessage";

type SubcontractorSectionProps = { order: Order };

const SubcontractorSection: React.FC<SubcontractorSectionProps> = (props) => {
  const { order } = props;
  const [showEditPrimaryCompanyDialog, setShowEditPrimaryCompanyDialog] = useState(false);
  const [showEditSecondaryCompanyDialog, setShowEditSecondaryCompanyDialog] = useState(false);
  const isAdmin = useAppSelector(selectUserIsAdminOrUserAdmin);
  const [editSubcontractor] = useEditSubcontractorMutation();
  const [assignCompany] = useAssignCompanyMutation();
  const [unassignCompany] = useUnassignCompanyMutation();

  return (
    <Box px={2} py={1}>
      <Typography sx={{ fontSize: "small" }}>Primär</Typography>

      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {order.assignedToCompany && (
          <Chip
            label={order.assignedToCompany}
            sx={{ fontSize: "1rem" }}
            onDelete={
              isAdmin
                ? () => {
                    editSubcontractor({
                      orderId: order.ordernr,
                      assignedToCompany: "",
                    });
                  }
                : undefined // Hide delete button
            }
          />
        )}

        {!order.assignedToCompany && !isAdmin && <EmptyMessage>Ingen vald</EmptyMessage>}

        {!order.assignedToCompany && isAdmin && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowEditPrimaryCompanyDialog(true)}
          >
            Lägg till
          </Button>
        )}
      </Box>

      <Typography sx={{ fontSize: "small", mt: 1 }}>Sekundära</Typography>

      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {[...order.additionalCompanies]
          .sort((a, b) => (a.uecode ?? "").localeCompare(b.uecode ?? ""))
          .map((company) => (
            <Chip
              key={company.id}
              label={company.uecode}
              sx={{ fontSize: "1rem" }}
              onDelete={
                isAdmin
                  ? () => {
                      unassignCompany({
                        orderId: order.ordernr,
                        companyId: company.id,
                      });
                    }
                  : undefined // Hide delete button
              }
            />
          ))}

        {order.additionalCompanies.length === 0 && !isAdmin && (
          <EmptyMessage>Ingen vald</EmptyMessage>
        )}

        {isAdmin && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowEditSecondaryCompanyDialog(true)}
          >
            Lägg till
          </Button>
        )}
      </Box>

      {showEditPrimaryCompanyDialog && (
        <SelectCompanyDialog
          title="Lägg till primär UE"
          order={order}
          onClose={() => setShowEditPrimaryCompanyDialog(false)}
          onSelect={(company) => {
            editSubcontractor({ orderId: order.ordernr, assignedToCompany: company.ueCode! });
          }}
        />
      )}

      {showEditSecondaryCompanyDialog && (
        <SelectCompanyDialog
          title="Lägg till sekundär UE"
          order={order}
          onClose={() => setShowEditSecondaryCompanyDialog(false)}
          onSelect={(company) =>
            assignCompany({
              orderId: order.ordernr,
              companyId: company.id,
              uecode: company.ueCode || "",
            })
          }
        />
      )}
    </Box>
  );
};

export default SubcontractorSection;
